import angular from "angular";
import { RecordDataSource } from "../../../records/grids";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../records/grids/columns/filters/subFilters/LookupFilter";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  vsName as DoubleClickHandlerServiceName,
  DoubleClickHandlerService,
  CellDoubleClickedEventHandlerArgs,
} from "../../../records/grids/cells/doubleClickHandlerService";

import { PatchObject, Record } from "../../../records/server/crud";
import { ICrudRecordResponse } from "../../../records/server/query";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import {
  booleanValueGetterFactory,
  lookupValueGetterFactory,
} from "../../../records/grids/cells/valueGetters";
import { OptionsFilter } from "../../../records/grids/columns/filters/subFilters/OptionsFilter";
import { ColDef } from "../../../records/grids/columns/colDef";
import { ProgressivePermissions } from "../../permissions";

const gridId = Symbol("progressiveMeterReadingSettings");
const progressiveMeterReadingSettingsUrl =
  "/api/machines/progressives/ProgressiveMeterReadingSetting";

export class ProgressiveMeterReadingSettingsViewController {
  static $inject = [
    GridSettingsServiceName,
    "$compile",
    ServerUtilityName,
    DoubleClickHandlerServiceName,
  ];

  gridDataSource: RecordDataSource;
  gridSettings: IGridSettings;
  columns: ColDef[];

  constructor(
    gridSettingsService: GridSettingsService,
    $compile: angular.ICompileService,
    private readonly serverUtility: ServerUtility,
    doubleClickHandlerService: DoubleClickHandlerService
  ) {
    this.gridDataSource = new RecordDataSource(
      serverUtility,
      progressiveMeterReadingSettingsUrl,
      true
    );
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    this.columns = [
      {
        field: "Id",
        hide: true,
        headerName: "Id",
        filter: new FilterProvider("Id", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
      },
      {
        field: "Name",
        headerName: "Name",
        filter: new FilterProvider("Name", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["Id"],
              serverQuery: (search: string) =>
                serverUtility.lookupQueryWrapped({
                  url: progressiveMeterReadingSettingsUrl,
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          patchField: {
            formField: {
              key: "Name",
              name: "Name",
              type: "gridInput",
              templateOptions: {
                label: "Name",
                required: true,
              },
              modelOptions: {
                debounce: 500,
              },
              asyncValidators: {
                unique: ($viewValue, $modelValue, $scope) => {
                  const id = $scope.model?.Id;
                  return serverUtility
                    .alreadyExistsWrapped({
                      url: progressiveMeterReadingSettingsUrl,
                      query: {
                        Name: $viewValue,
                      },
                      excludeId: id,
                    })
                    .then((alreadyExists) => {
                      if (alreadyExists) {
                        throw new Error();
                      }
                    });
                },
              },
            },
          },
          submit: (patchObject) => {
            return serverUtility.patch({
              url: progressiveMeterReadingSettingsUrl,
              patchObject,
            });
          },
          fetchRecord: (args) => this.fetchRecord(args),
        }),
      },
      {
        field: "MeterReadingRequired",
        headerName: "Meter Reading Required",
        valueGetter: booleanValueGetterFactory(),
        filter: new FilterProvider("MeterReadingRequired", $compile)
          .defaultBooleanFilters()
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.simpleBoolField({
                key: "MeterReadingRequired",
                label: "Meter Reading Requried",
                name: "MeterReadingRequired",
              }),
              type: "gridCheckbox",
            },
            referenceFieldFormatters: [
              (data: unknown) => (data ? "Yes" : "No"),
            ],
          },
        }),
      },
      {
        field: "ComparisonField",
        headerName: "Comparison Field",
        valueGetter: lookupValueGetterFactory({
          lookup: { ResetBase: "Reset Base", LevelReading: "Level Reading" },
        }),
        filter: new FilterProvider("ComparisonField", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                { display: "Level Reading", value: "LevelReading" },
                { display: "Reset Base", value: "ResetBase" },
              ],
              queryFieldNames: ["ComparisonField"],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              key: "ComparisonField",
              name: "ComparisonField",
              type: "gridSelect",
              templateOptions: {
                label: "Comparison Field",
                options: [
                  {
                    label: "Level Reading",
                    value: "LevelReading",
                  },
                  {
                    label: "Reset Base",
                    value: "ResetBase",
                  },
                ],
                required: true,
                labelProp: "label",
                valueProp: "value",
              },
            },
            referenceFieldFormatters: [
              (value) =>
                value
                  ? value === "LevelReading"
                    ? "Level Reading"
                    : "Reset Base"
                  : undefined,
            ],
          },
        }),
      },
      {
        field: "ComparisonValue",
        headerName: "Comparison Value",
        filter: new FilterProvider("ComparisonValue", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.simpleNumberField({
                key: "ComparisonValue",
                name: "ComparisonValue",
                label: "Comparison Value",
                min: 0,
                step: 1,
              }),
              type: "gridInput",
            },
          },
        }),
      },
      {
        field: "EveryXDays",
        headerName: "Every X Days",
        filter: new FilterProvider("EveryXDays", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.simpleNumberField({
                key: "EveryXDays",
                label: "Every X Days",
                min: 1,
                max: 1,
                required: true,
                name: "EveryXDays",
                step: 1,
              }),
              type: "gridInput",
            },
          },
        }),
      },
      {
        field: "AllReadingsRequiredDayOfMonth",
        headerName: "All Readings Required Day Of Month",
        filter: new FilterProvider("AllReadingsRequiredDayOfMonth", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.simpleNumberField({
                key: "AllReadingsRequiredDayOfMonth",
                name: "AllReadingsRequiredDayOfMonth",
                label: "All Readings Required Day Of Month",
                min: -28,
                max: 28,
              }),
              type: "gridInput",
            },
          },
        }),
      },
    ];
  }

  patchRecord(
    patchObject: PatchObject
  ): angular.IPromise<ICrudRecordResponse<Record>> {
    return this.serverUtility.patch({
      url: progressiveMeterReadingSettingsUrl,
      patchObject,
    });
  }
  fetchRecord(
    args: CellDoubleClickedEventHandlerArgs
  ): angular.IPromise<Record | undefined> {
    return this.serverUtility.querySingleWrapped({
      url: progressiveMeterReadingSettingsUrl,
      query: {
        Id: args.data.Id,
      },
    });
  }
}
